
import React from 'react';
import { Poll } from '@/types/poll';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Eye,
  Edit,
  Trash2,
  Check,
  X,
  RefreshCw,
  Link as LinkIcon,
  Image,
} from 'lucide-react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';

interface PollTableProps {
  polls: Poll[];
  isLoading: boolean;
  onEdit: (poll: Poll) => void;
  onDelete: (pollId: string) => void;
  onActivate: (pollId: string) => void;
  onClose: (pollId: string) => void;
  onReopen: (pollId: string) => void;
  onPreview: (poll: Poll) => void;
  onLinkToCard?: (poll: Poll) => void;
}

const PollTable: React.FC<PollTableProps> = ({
  polls,
  isLoading,
  onEdit,
  onDelete,
  onActivate,
  onClose,
  onReopen,
  onPreview,
  onLinkToCard,
}) => {
  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'MMM d, yyyy');
  };

  // If loading, show loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        <span className="ml-3">Loading polls...</span>
      </div>
    );
  }

  // If no polls, show empty state
  if (polls.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No polls found. Create your first poll to get started!</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[300px]">Title</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Poll #</TableHead>
            <TableHead>Responses</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>Closes</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {polls.map((poll) => (
            <TableRow key={poll.id}>
              <TableCell className="font-medium">
                <div className="flex items-center gap-3">
                  {/* Small image thumbnail if poll has image */}
                  {poll.image_url ? (
                    <div className="h-10 w-10 rounded-md overflow-hidden flex-shrink-0 border">
                      <img
                        src={poll.image_url}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="h-10 w-10 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Image size={16} className="text-slate-400" />
                    </div>
                  )}
                  <span className="line-clamp-2">{poll.title}</span>
                </div>
              </TableCell>
              <TableCell>
                {poll.status === 'live' ? (
                  <Badge className="bg-green-500">Active</Badge>
                ) : poll.status === 'closed' ? (
                  <Badge className="bg-gray-500">Closed</Badge>
                ) : (
                  <Badge variant="outline">Draft</Badge>
                )}
              </TableCell>
              <TableCell>
                <span className="text-xs font-mono">{poll.poll_number || '-'}</span>
              </TableCell>
              <TableCell>{poll.total_votes || 0}</TableCell>
              <TableCell>{formatDate(poll.created_at)}</TableCell>
              <TableCell>{formatDate(poll.closes_at)}</TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Button variant="ghost" size="icon" onClick={() => onPreview(poll)}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => onEdit(poll)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  
                  {/* Status action buttons */}
                  {poll.status === 'draft' && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => onActivate(poll.id)}
                      title="Activate Poll"
                    >
                      <Check className="h-4 w-4 text-green-500" />
                    </Button>
                  )}
                  
                  {poll.status === 'live' && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => onClose(poll.id)}
                      title="Close Poll"
                    >
                      <X className="h-4 w-4 text-gray-500" />
                    </Button>
                  )}
                  
                  {poll.status === 'closed' && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => onReopen(poll.id)}
                      title="Reopen Poll"
                    >
                      <RefreshCw className="h-4 w-4 text-blue-500" />
                    </Button>
                  )}
                  
                  {/* Link to card button */}
                  {onLinkToCard && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => onLinkToCard(poll)}
                      title="Link to Insight Card"
                    >
                      <LinkIcon className="h-4 w-4" />
                    </Button>
                  )}
                  
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => onDelete(poll.id)} 
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default PollTable;
